import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ReviewGeneration = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [dashboard, setDashboard] = useState(null);
  const [requests, setRequests] = useState([]);
  const [qrCodes, setQrCodes] = useState([]);
  const [reviewLogs, setReviewLogs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newRequest, setNewRequest] = useState({
    patient_name: '', patient_email: '', patient_phone: '',
    appointment_date: new Date().toISOString().split('T')[0],
    service_type: 'General Visit', preferred_platform: 'google'
  });
  const [responseForm, setResponseForm] = useState({ review_text: '', rating: 5, reviewer_name: '' });
  const [suggestedResponse, setSuggestedResponse] = useState('');
  const [generating, setGenerating] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [dashRes, reqRes, qrRes, logsRes] = await Promise.all([
        fetch(`${API_URL}/api/reviews/dashboard`),
        fetch(`${API_URL}/api/reviews/requests?limit=50`),
        fetch(`${API_URL}/api/reviews/qr-codes/all`),
        fetch(`${API_URL}/api/reviews/logs`)
      ]);
      if (dashRes.ok) setDashboard(await dashRes.json());
      if (reqRes.ok) setRequests((await reqRes.json()).requests || []);
      if (qrRes.ok) setQrCodes((await qrRes.json()).qr_codes || []);
      if (logsRes.ok) setReviewLogs(await logsRes.json());
    } catch (err) { console.error(err); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const createRequest = async () => {
    try {
      await fetch(`${API_URL}/api/reviews/requests`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newRequest)
      });
      setNewRequest({ patient_name: '', patient_email: '', patient_phone: '', appointment_date: new Date().toISOString().split('T')[0], service_type: 'General Visit', preferred_platform: 'google' });
      fetchData();
    } catch (err) { alert('Failed to create request'); }
  };

  const sendRequest = async (id) => {
    await fetch(`${API_URL}/api/reviews/send/${id}`, { method: 'POST' });
    fetchData();
  };

  const markCompleted = async (id) => {
    await fetch(`${API_URL}/api/reviews/requests/${id}/status`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: 'completed' })
    });
    fetchData();
  };

  const generateResponse = async () => {
    setGenerating(true);
    try {
      const res = await fetch(`${API_URL}/api/reviews/suggest-response`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(responseForm)
      });
      if (res.ok) {
        const data = await res.json();
        setSuggestedResponse(data.suggested_response);
      }
    } catch (err) { console.error(err); }
    setGenerating(false);
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-700',
      sent: 'bg-blue-100 text-blue-700',
      reminded: 'bg-purple-100 text-purple-700',
      completed: 'bg-green-100 text-green-700',
      opted_out: 'bg-gray-100 text-gray-600'
    };
    return <span className={`px-2 py-1 rounded text-xs font-medium ${styles[status] || styles.pending}`}>{status}</span>;
  };

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: '📊' },
    { id: 'requests', label: 'Review Requests', icon: '📧' },
    { id: 'qr-codes', label: 'QR Codes', icon: '📱' },
    { id: 'monitor', label: 'Monitor Reviews', icon: '👁️' },
    { id: 'respond', label: 'AI Responses', icon: '🤖' },
  ];

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div></div>;

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet><title>Review Generation | Admin</title></Helmet>

      <div className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white px-6 py-4">
        <h1 className="text-2xl font-bold">⭐ Review Generation System</h1>
        <p className="text-yellow-100 text-sm">Automate review requests and monitor your online reputation</p>
      </div>

      <div className="border-b border-gray-200 px-6 bg-white">
        <nav className="flex space-x-6">
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`py-4 px-2 border-b-2 font-medium text-sm transition ${activeTab === tab.id ? 'border-orange-500 text-orange-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
              {tab.icon} {tab.label}
            </button>
          ))}
        </nav>
      </div>

      <div className="p-6">
        {activeTab === 'dashboard' && dashboard && (
          <div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
              <div className="bg-white p-4 rounded-lg shadow border-l-4 border-blue-500">
                <p className="text-gray-500 text-sm">Total Requests</p>
                <p className="text-2xl font-bold">{dashboard.stats.total_requests}</p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow border-l-4 border-green-500">
                <p className="text-gray-500 text-sm">Sent</p>
                <p className="text-2xl font-bold text-green-600">{dashboard.stats.sent}</p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow border-l-4 border-yellow-500">
                <p className="text-gray-500 text-sm">Completed</p>
                <p className="text-2xl font-bold text-yellow-600">{dashboard.stats.completed}</p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow border-l-4 border-purple-500">
                <p className="text-gray-500 text-sm">Conversion</p>
                <p className="text-2xl font-bold text-purple-600">{dashboard.stats.conversion_rate}%</p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow border-l-4 border-orange-500">
                <p className="text-gray-500 text-sm">Pending</p>
                <p className="text-2xl font-bold text-orange-600">{dashboard.stats.pending}</p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow border-l-4 border-gray-500">
                <p className="text-gray-500 text-sm">Opted Out</p>
                <p className="text-2xl font-bold text-gray-600">{dashboard.stats.opted_out}</p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="font-semibold text-gray-800 mb-4">Platform Stats</h3>
                {dashboard.platforms?.map(p => (
                  <div key={p.id} className="flex justify-between items-center py-2 border-b last:border-0">
                    <span>{p.icon} {p.name}</span>
                    <div className="text-right">
                      <span className="text-yellow-500">★ {p.current_rating || 'N/A'}</span>
                      <span className="text-gray-500 text-sm ml-2">({p.review_count || 0} reviews)</span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="font-semibold text-gray-800 mb-4">Recent Requests</h3>
                {dashboard.recent_requests?.slice(0, 5).map(r => (
                  <div key={r.id} className="flex justify-between items-center py-2 border-b last:border-0">
                    <span className="text-sm">{r.patient_name}</span>
                    {getStatusBadge(r.status)}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'requests' && (
          <div>
            <div className="bg-white rounded-lg shadow p-6 mb-6">
              <h3 className="font-semibold text-gray-800 mb-4">Add New Review Request</h3>
              <div className="grid md:grid-cols-3 gap-4">
                <input placeholder="Patient Name" value={newRequest.patient_name} onChange={e => setNewRequest({...newRequest, patient_name: e.target.value})} className="border rounded px-3 py-2" />
                <input type="email" placeholder="Email" value={newRequest.patient_email} onChange={e => setNewRequest({...newRequest, patient_email: e.target.value})} className="border rounded px-3 py-2" />
                <input placeholder="Phone (optional)" value={newRequest.patient_phone} onChange={e => setNewRequest({...newRequest, patient_phone: e.target.value})} className="border rounded px-3 py-2" />
                <input type="date" value={newRequest.appointment_date} onChange={e => setNewRequest({...newRequest, appointment_date: e.target.value})} className="border rounded px-3 py-2" />
                <select value={newRequest.service_type} onChange={e => setNewRequest({...newRequest, service_type: e.target.value})} className="border rounded px-3 py-2">
                  <option>General Visit</option><option>Dental Implants</option><option>Invisalign</option><option>LANAP</option><option>Cosmetic</option><option>Emergency</option>
                </select>
                <select value={newRequest.preferred_platform} onChange={e => setNewRequest({...newRequest, preferred_platform: e.target.value})} className="border rounded px-3 py-2">
                  <option value="google">🔵 Google</option><option value="yelp">🔴 Yelp</option><option value="healthgrades">💚 Healthgrades</option><option value="facebook">🔷 Facebook</option>
                </select>
              </div>
              <button onClick={createRequest} className="mt-4 bg-orange-500 text-white px-6 py-2 rounded hover:bg-orange-600">Add Request</button>
            </div>

            <div className="bg-white rounded-lg shadow">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Patient</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Service</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Platform</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {requests.map(r => (
                    <tr key={r.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium">{r.patient_name}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{r.patient_email}</td>
                      <td className="px-4 py-3 text-sm">{r.service_type}</td>
                      <td className="px-4 py-3 text-sm capitalize">{r.preferred_platform}</td>
                      <td className="px-4 py-3">{getStatusBadge(r.status)}</td>
                      <td className="px-4 py-3">
                        {r.status === 'pending' && <button onClick={() => sendRequest(r.id)} className="text-blue-600 hover:text-blue-800 text-sm mr-2">Send</button>}
                        {r.status === 'sent' && <button onClick={() => markCompleted(r.id)} className="text-green-600 hover:text-green-800 text-sm">Mark Complete</button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'qr-codes' && (
          <div>
            <div className="bg-white rounded-lg shadow p-6 mb-6">
              <h3 className="font-semibold text-gray-800 mb-2">📱 Printable QR Codes</h3>
              <p className="text-gray-600 text-sm mb-4">Print these QR codes for your office - patients can scan to leave a review instantly!</p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {qrCodes.map(qr => (
                <div key={qr.platform_id} className="bg-white rounded-lg shadow p-6 text-center">
                  <div className="text-3xl mb-2">{qr.icon}</div>
                  <h4 className="font-semibold text-lg mb-4">{qr.platform_name}</h4>
                  <img src={qr.qr_code_url} alt={`${qr.platform_name} QR Code`} className="mx-auto mb-4 border rounded" />
                  <p className="text-xs text-gray-500 mb-2">{qr.instructions}</p>
                  <a href={qr.review_url} target="_blank" rel="noreferrer" className="text-teal-600 hover:underline text-sm">Test Link →</a>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'monitor' && reviewLogs && (
          <div>
            <div className="grid md:grid-cols-4 gap-4 mb-6">
              <div className="bg-white p-4 rounded-lg shadow">
                <p className="text-gray-500 text-sm">Total Reviews Logged</p>
                <p className="text-2xl font-bold">{reviewLogs.total}</p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow">
                <p className="text-gray-500 text-sm">Average Rating</p>
                <p className="text-2xl font-bold text-yellow-500">★ {reviewLogs.average_rating}</p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow">
                <p className="text-gray-500 text-sm">Pending Responses</p>
                <p className="text-2xl font-bold text-orange-600">{reviewLogs.pending_responses}</p>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow">
              <div className="p-4 border-b"><h3 className="font-semibold">Recent Reviews</h3></div>
              <div className="divide-y">
                {reviewLogs.reviews?.map(r => (
                  <div key={r.id} className="p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <span className="font-medium">{r.reviewer_name}</span>
                        <span className="text-gray-500 text-sm ml-2">on {r.platform}</span>
                      </div>
                      <span className="text-yellow-500">{'★'.repeat(r.rating)}</span>
                    </div>
                    <p className="text-gray-600 text-sm mt-2">{r.review_text}</p>
                  </div>
                ))}
                {(!reviewLogs.reviews || reviewLogs.reviews.length === 0) && <div className="p-8 text-center text-gray-500">No reviews logged yet. Add reviews from the Monitor tab.</div>}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'respond' && (
          <div className="max-w-2xl">
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="font-semibold text-gray-800 mb-4">🤖 AI Response Generator</h3>
              <p className="text-gray-600 text-sm mb-4">Paste a review and get a professional, personalized response suggestion.</p>
              <div className="space-y-4">
                <input placeholder="Reviewer Name" value={responseForm.reviewer_name} onChange={e => setResponseForm({...responseForm, reviewer_name: e.target.value})} className="w-full border rounded px-3 py-2" />
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Rating</label>
                  <div className="flex gap-2">
                    {[1,2,3,4,5].map(n => (
                      <button key={n} onClick={() => setResponseForm({...responseForm, rating: n})} className={`px-3 py-1 rounded ${responseForm.rating >= n ? 'bg-yellow-400 text-white' : 'bg-gray-200'}`}>★</button>
                    ))}
                  </div>
                </div>
                <textarea placeholder="Paste the review text here..." value={responseForm.review_text} onChange={e => setResponseForm({...responseForm, review_text: e.target.value})} rows={4} className="w-full border rounded px-3 py-2" />
                <button onClick={generateResponse} disabled={generating || !responseForm.review_text} className="w-full bg-orange-500 text-white px-4 py-2 rounded hover:bg-orange-600 disabled:opacity-50">
                  {generating ? '🧠 Generating...' : '✨ Generate Response'}
                </button>
              </div>
              {suggestedResponse && (
                <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <h4 className="font-semibold text-green-800 mb-2">Suggested Response:</h4>
                  <p className="text-gray-700 whitespace-pre-wrap">{suggestedResponse}</p>
                  <button onClick={() => navigator.clipboard.writeText(suggestedResponse)} className="mt-2 text-teal-600 text-sm hover:underline">Copy to Clipboard</button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReviewGeneration;
